import type { ReadonlyNonEmptyArray } from '../../../../../../utils'

import { areCybusNodesEquals, Capability, type StatusType } from '../../../../../../domain'

import type { PageSubscriptionsTypes, SubscriptionFilterArgs } from '../../../../../../application'

import { mapResourceName, type Resource } from '../../../../mappers'
import {
    type AllResourcesRequestConfig,
    BaseStatefulPageResourceSubscriptionStrategy,
    type PageResourceQueryParameters,
    type PageResourceSortableColumnsMap,
    type ServiceResourcesRequestConfig,
} from '../Resource'

export class NodesStrategy extends BaseStatefulPageResourceSubscriptionStrategy<'Node', 'nodes'> {
    protected override readonly supportedFilters: (keyof SubscriptionFilterArgs)[] = ['server', 'service']

    protected override readonly areEquals = areCybusNodesEquals

    protected override readonly allResourcesRequestConfig: AllResourcesRequestConfig<'nodes'> = {
        capability: Capability.NODES_READ,
        path: '/api/v2/resources',
        method: 'GET',
    }

    protected override readonly resourceCategories: ReadonlyNonEmptyArray<'Node'> = ['Node']

    protected override readonly resourceStatusType = 'nodes' as const

    protected override readonly serviceDeviationsType = ['nodes', 'nodes'] as const

    protected override readonly serverErrorMessage = 'Could not fetch nodes'

    protected sortingColumns: PageResourceSortableColumnsMap<'nodes'> = {
        id: 'resourceId',
        name: 'resourceName',
        operation: null,
        parent: 'parentResourceId',
        protocol: 'protocol',
        service: 'serviceId',
        status: null,
    }

    override readonly requiredPermissions = [Capability.NODES_READ]

    protected readonly serviceResourcesRequestConfig: ServiceResourcesRequestConfig<'nodes'> = {
        capability: Capability.NODES_READ,
        path: '/api/v2/services/+/resources',
        method: 'GET',
    }

    protected override mapResource (resource: Resource<'Node'>, status: StatusType): PageSubscriptionsTypes['nodes'] {
        return {
            id: resource.resourceId,
            service: resource.serviceId,
            name: mapResourceName(resource.resourceId),
            parent: resource.parentResourceId,
            operation: resource.resourceConfig.args.operation || null,
            protocol: resource.protocol,
            status,
        }
    }

    protected override createCustomQueryParameters ({ server }: SubscriptionFilterArgs): PageResourceQueryParameters {
        return server ? { parentResourceId: server } : {}
    }
}
